import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ApiError,
  isOrphanedAttribute,
  strandedHeldCount,
  structureRemapRequiredRefusal,
  type AtRiskNode,
  type PendingUpgradeView,
  type UpgradeDriftReport,
} from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { StructureRemapTable, describeUpgradeEntry, type ItemNoun, type SegmentNoun } from "@keydials/dial-workspace";
import { StrandedStatusesNotice } from "./StrandedStatusesNotice";
import { dismissNotice, isNoticeDismissed } from "./upgradeNoticeDismissal";

// Spreadsheet-import Phase 2b: the notice a dial admin sees on their live dial when the platform admin has
// queued a new definition version. Reads the frozen drift snapshot to show what's changing and what data would
// be cleared, and lets an optional upgrade be accepted/declined and the at-risk data exported first.

const CHANGE_GLYPH: Record<string, { glyph: string; colour: string }> = {
  Added: { glyph: "+", colour: "var(--st-done, #2e8b57)" },
  Changed: { glyph: "~", colour: "var(--st-progress, #b45309)" },
  Removed: { glyph: "–", colour: "var(--st-risk, #c14257)" },
};


/** Mirrors the server's early-apply cascade guard: a group update that would clear data in any child dial (at any
 *  nesting depth) can only go on its scheduled date, because those children's own dial admins were never sent a
 *  notice and the wait is their one chance to export. Offering the button here would just earn a 409. */
export function cascadeClearsChildData(report: UpgradeDriftReport): boolean {
  return (report.children ?? []).some(
    (c) => !!c.drift
      && (c.drift.atRisk.some((n) => n.total > 0 && !isOrphanedAttribute(n)) || cascadeClearsChildData(c.drift)),
  );
}

function describeAtRisk(n: AtRiskNode): string {
  const parts: string[] = [];
  if (n.documentCount) parts.push(`${n.documentCount} ${n.documentCount === 1 ? "file" : "files"}`);
  if (n.statusCount) parts.push(`${n.statusCount} ${n.statusCount === 1 ? "status" : "statuses"}`);
  if (n.historyCount) parts.push(`${n.historyCount} history`);
  if (n.attributeCount) parts.push(`${n.attributeCount} ${n.attributeCount === 1 ? "field" : "fields"}`);
  return parts.join(" · ");
}

/** How many nodes this entry's orphaned values sit on — an "Attribute" entry counts values, not records. */
function describeOrphanedAttribute(n: AtRiskNode): string {
  return `${n.attributeCount} ${n.attributeCount === 1 ? "value" : "values"} already filled in`;
}

/** A stable list key: at-risk entries are unique by node path, and (KEY-280) by attribute key on top of it. */
export function atRiskKey(n: AtRiskNode): string {
  return `${n.sectionKey}-${n.segmentKey ?? ""}-${n.itemKey ?? ""}-${n.dimensionKey ?? ""}-${n.attributeKey ?? ""}`;
}

/** The hash a notification links to (`/instances/{id}#upgrade`): the notice shows even if dismissed, and scrolls into view. */
export const UPGRADE_NOTICE_HASH = "#upgrade";

/** KEY-807: the structure nodes the mapping table now answers, which the old "will remove data" box would only repeat. */
const STRUCTURE_LEVELS = new Set(["Section", "Segment", "Item"]);

export function UpgradeNotice({ instanceId, onApplied, noun, itemNounFor }: {
  instanceId: string;
  onApplied?: () => void | Promise<void>;
  /** KEY-721: this dial's word for an action point ("list" on a family dial). */
  noun?: SegmentNoun;
  /** KEY-753: a section's word for its items ("appointment"), for the mapping table. */
  itemNounFor?: (sectionKey: string) => ItemNoun;
}) {
  const [view, setView] = useState<PendingUpgradeView | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState<null | "accept" | "decline" | "export" | "apply">(null);
  const [confirmApply, setConfirmApply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // With the mapping table showing, the full change list folds away so its question is the first thing read.
  const [showChanges, setShowChanges] = useState(false);
  // Opened from a notification (KEY-807): show the notice even if "Got it" hid it, and bring it into view.
  const linked = useLocation().hash === UPGRADE_NOTICE_HASH;

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setView(await api.getPendingUpgrade(token, instanceId));
    } catch {
      // 404 (feature off) or any read error → no notice.
      setView(null);
    }
  }, [instanceId]);

  useEffect(() => {
    setDismissed(false);
    setConfirmApply(false);
    void load();
  }, [load]);

  useEffect(() => {
    if (linked && view) document.getElementById("upgrade-notice")?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [linked, view]);

  if (!view || (!linked && (dismissed || isNoticeDismissed(instanceId, view)))) return null;

  const mandatory = view.kind === "Mandatory";
  const accepted = view.status === "Accepted";
  // Held server-side until the platform admin says what the statuses this version drops become. Accepting or applying
  // would be refused, so neither is offered — the dial admin is told it's being prepared instead.
  const needsMapping = view.needsStatusMapping === true;
  // KEY-806: held server-side until the dial admin says where the lists and items this version removes go, which they do in
  // the mapping table (KEY-807). Accepting or applying would be refused, so neither is offered; declining still is.
  const needsStructureMapping = view.needsStructureMapping === true;
  const drift = view.drift;
  const version = drift?.toVersion ?? "the new version";
  // KEY-807: the dial admin's mapping table, read live. Where it's shown, it is the account of the lists and items that go.
  const structure = view.structureMapping && view.structureMapping.atRisk.length > 0 ? view.structureMapping : null;
  // Two different fates, so two lists: `atRisk` is deleted on apply and drives the export + confirm gate;
  // `orphaned` is a field the new version stops declaring, whose values survive but stop being shown
  // (KEY-280). Lumping them together would either overstate the second or understate the first.
  const reported = (drift?.atRisk ?? []).filter((n) => n.total > 0);
  const atRisk = reported.filter((n) => !isOrphanedAttribute(n) && !(structure && STRUCTURE_LEVELS.has(n.nodeType)));
  // What an apply clears: the box's entries, plus whatever the saved map lets go.
  const discarded = structure ? structure.map.filter((e) => e.action === "Discard").length : 0;
  const clears = atRisk.length + discarded;
  const orphaned = reported.filter(isOrphanedAttribute);
  const effective = new Date(view.effectiveDateUtc).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  // Optional updates can be brought forward (KEY-240) unless the group cascade would clear a child dial's data.
  const canApplyNow = !mandatory && !needsMapping && !needsStructureMapping && !(drift && cascadeClearsChildData(drift));

  // KEY-806/807: an accept or apply held for a structure map. The dial changed since the notice was read (something new holds
  // data, say), so re-read it: the table then lists what still needs a place. Not the status-map refusal, which is the admin's.
  async function heldForMapping(e: unknown): Promise<boolean> {
    const held = structureRemapRequiredRefusal(e);
    if (!held) return false;
    await load();
    setError(held.unanswered.length > 0
      ? `Choose where ${held.unanswered.length === 1 ? "1 more thing goes" : `${held.unanswered.length} more things go`} first. The list above is up to date.`
      : "The update can't go ahead with your choices as they are. Nothing has changed. Look over the list above and save again.");
    return true;
  }

  async function act(kind: "accept" | "decline") {
    const token = getAccessToken();
    if (!token) return;
    setBusy(kind);
    setError(null);
    try {
      if (kind === "accept") {
        await api.acceptUpgrade(token, instanceId, view!.id);
        await load();
      } else {
        await api.declineUpgrade(token, instanceId, view!.id);
        setView(null);
      }
    } catch (e) {
      if (!(await heldForMapping(e))) setError(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(null);
    }
  }

  // Bring the update forward. Irreversible in exactly the way the scheduled apply is — the removed nodes' data is
  // hard-deleted and the files with it — so anything at risk is confirm-gated (and the export offered) first.
  async function applyNow() {
    const token = getAccessToken();
    if (!token) return;
    setBusy("apply");
    setError(null);
    try {
      await api.applyUpgradeNow(token, instanceId, view!.id);
      setView(null);
      setConfirmApply(false);
      // The dial's structure just changed underneath the workspace, so re-reading the notice isn't enough:
      // the host has to refetch the resolved dial.
      await onApplied?.();
    } catch (e) {
      if (!(await heldForMapping(e))) setError(e instanceof ApiError ? e.message : "The update couldn't be applied. Try again.");
      setConfirmApply(false);
    } finally {
      setBusy(null);
    }
  }

  async function exportAtRisk() {
    const token = getAccessToken();
    if (!token) return;
    setBusy("export");
    setError(null);
    try {
      const data = await api.exportUpgradeAtRisk(token, instanceId, view!.targetVersionId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.instanceName.replace(/[^\w.-]+/g, "-")}-at-risk-v${data.fromVersion}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The export couldn't be downloaded. Try again.");
    } finally {
      setBusy(null);
    }
  }

  const pillColour = mandatory ? "var(--st-risk, #7a3b9c)" : "var(--accent, #216e8b)";

  return (
    <div
      id="upgrade-notice"
      className="kd-card"
      style={{
        padding: "var(--pad-card, 16px)",
        margin: "0 0 14px",
        borderLeft: `3px solid ${pillColour}`,
      }}
      role="region"
      aria-label="Version upgrade notice"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
            color: "#fff", background: pillColour, borderRadius: 999, padding: "2px 9px",
          }}
        >
          {mandatory ? "Required update" : "Update available"}
        </span>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" }}>
          This dial will move to version {version}
        </h3>
      </div>

      <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 13.5 }}>
        {accepted
          ? canApplyNow
            ? `You accepted this update. It's scheduled for ${effective}, but nothing has changed yet — apply it now if you're ready.`
            : `You accepted this update — it's scheduled for ${effective}. Nothing changes until then.`
          : mandatory
            ? `Your organisation's dial is being upgraded on ${effective}.`
            : "A new version of this dial is available whenever you're ready."}
        {view.adminNote ? ` ${view.adminNote}` : ""}
      </p>

      {drift && drift.whatsNew.length > 0 && structure && (
        <button
          type="button"
          onClick={() => setShowChanges((v) => !v)}
          aria-expanded={showChanges}
          aria-controls="upgrade-changes"
          style={{ marginTop: 10, padding: 0, border: 0, background: "none", font: "inherit", fontSize: 12.5, color: "var(--accent, #216e8b)", cursor: "pointer", textDecoration: "underline" }}
        >
          {showChanges ? "Hide the changes" : `See all ${drift.whatsNew.length} ${drift.whatsNew.length === 1 ? "change" : "changes"}`}
        </button>
      )}

      {drift && drift.whatsNew.length > 0 && (!structure || showChanges) && (
        <ul
          id="upgrade-changes" style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 5 }}>
          {drift.whatsNew.map((e, i) => {
            const g = CHANGE_GLYPH[e.change] ?? CHANGE_GLYPH.Changed;
            return (
              <li key={`${e.level}-${e.key}-${i}`} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 13 }}>
                <span style={{ fontFamily: "var(--mono, monospace)", fontWeight: 700, color: g.colour, width: 10, textAlign: "center" }}>{g.glyph}</span>
                <span style={{ color: "var(--text)" }}>{describeUpgradeEntry(e, { list: noun })}</span>
              </li>
            );
          })}
        </ul>
      )}

      {atRisk.length > 0 && (
        <div
          style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8,
            border: "1px solid color-mix(in srgb, var(--st-risk, #c14257) 40%, var(--border))",
            background: "color-mix(in srgb, var(--st-risk, #c14257) 8%, transparent)",
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--st-risk, #c14257)" }}>
            This will remove data — export it first if you need a copy:
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 3 }}>
            {atRisk.map((n) => (
              <li key={atRiskKey(n)} style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                <b style={{ color: "var(--text)" }}>{n.label}</b> — {describeAtRisk(n)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {orphaned.length > 0 && (
        <div
          style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8,
            border: "1px solid color-mix(in srgb, var(--st-progress, #b45309) 40%, var(--border))",
            background: "color-mix(in srgb, var(--st-progress, #b45309) 8%, transparent)",
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--st-progress, #b45309)" }}>
            {orphaned.length === 1 ? "This field is going away" : "These fields are going away"} — what's already
            been entered stops being shown:
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 3 }}>
            {orphaned.map((n) => (
              <li key={atRiskKey(n)} style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                <b style={{ color: "var(--text)" }}>{n.label}</b> — {describeOrphanedAttribute(n)}
              </li>
            ))}
          </ul>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 5 }}>
            Nothing is deleted — the entries are kept and reappear if the field is put back. Ask whoever manages
            this dial's template if you were expecting to keep it.
          </div>
        </div>
      )}

      {structure && (
        <StructureRemapTable
          mapping={structure}
          droppedSectionScopes={drift?.droppedSectionScopes}
          save={(entries) => api.saveUpgradeStructureMap(getAccessToken() ?? "", instanceId, view.id, entries)}
          onSaved={load}
          onExport={exportAtRisk}
          exportBusy={busy === "export"}
          nouns={{ list: noun, itemFor: itemNounFor }}
        />
      )}

      <StrandedStatusesNotice drift={drift} version={version} noun={noun} />

      {drift?.children && drift.children.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}>
            This update also affects the group's subdials:
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 3 }}>
            {drift.children.map((ch) => {
              // Orphaned fields are excluded: this line is about records that get cleared.
              const atRiskTotal = ch.drift?.atRisk.reduce((n, a) => n + (isOrphanedAttribute(a) ? 0 : a.total), 0) ?? 0;
              const strandedCount = ch.drift?.strandedStatuses?.reduce((n, p) => n + strandedHeldCount(p), 0) ?? 0;
              return (
                <li key={ch.childDefinitionId} style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  <b style={{ color: "var(--text)" }}>{ch.name}</b>
                  {ch.change === "Upgraded" && ch.drift
                    ? ` — moves to version ${ch.drift.toVersion}${atRiskTotal > 0 ? ` (${atRiskTotal} record${atRiskTotal === 1 ? "" : "s"} would be cleared — export from that dial first)` : ""}${strandedCount > 0 ? `; ${strandedCount.toLocaleString("en-GB")} ${strandedCount === 1 ? "status isn't" : "statuses aren't"} in the new version` : ""}`
                    : ch.change === "Removed"
                      ? " — leaves the group; its data is kept, just hidden"
                      : " — joins the group as a new dial"}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {needsMapping && (
        <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          Key Dials is still working out what the statuses this version changes become on your dial, so the update can't
          be accepted or applied yet. Nothing on the dial changes until it can.
        </p>
      )}

      {needsStructureMapping && !needsMapping && (
        <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          {structure
            ? "This update can't be accepted or applied until everything above has a place, or you've chosen to let it go. Nothing on the dial changes until then."
            : "This update removes lists or items that still hold data, so it can't be accepted or applied until you've said where each one goes. Nothing on the dial changes until then."}
        </p>
      )}

      {error && <p style={{ margin: "10px 0 0", color: "var(--st-risk, #c14257)", fontSize: 12.5 }}>{error}</p>}

      {confirmApply && (
        <div
          style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8,
            border: "1px solid color-mix(in srgb, var(--st-risk, #c14257) 55%, var(--border))",
            background: "color-mix(in srgb, var(--st-risk, #c14257) 10%, transparent)",
          }}
          role="alertdialog"
          aria-label="Confirm applying the update now"
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--st-risk, #c14257)" }}>
            This permanently deletes data. It can't be undone.
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
            Applying now moves this dial to version {version} straight away and clears{" "}
            {structure
              ? `the ${clears === 1 ? "thing" : `${clears} things`} you chose to let go`
              : `the ${atRisk.length} ${atRisk.length === 1 ? "item" : "items"} listed above`} — their statuses, history, fields and files are
            deleted for good. Export them first if you might need them.
          </p>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        {clears > 0 && (
          <button onClick={() => void exportAtRisk()} disabled={busy !== null} className="kd-btn-dashed" style={{ fontSize: 13 }}>
            {busy === "export" ? "Preparing…" : "Export data at risk"}
          </button>
        )}
        {confirmApply ? (
          <>
            <button onClick={() => void applyNow()} disabled={busy !== null} className="kd-btn-primary" style={{ fontSize: 13 }}>
              {busy === "apply" ? "Applying…" : "Yes, apply now and delete the data"}
            </button>
            <button onClick={() => setConfirmApply(false)} disabled={busy !== null} className="kd-btn-dashed" style={{ fontSize: 13 }}>
              Keep the scheduled date
            </button>
          </>
        ) : (
          <>
            {!mandatory && !accepted && !needsMapping && (
              <>
                {!needsStructureMapping && (
                  <button onClick={() => void act("accept")} disabled={busy !== null} className="kd-btn-primary" style={{ fontSize: 13 }}>
                    {busy === "accept" ? "…" : "Accept update"}
                  </button>
                )}
                <button onClick={() => void act("decline")} disabled={busy !== null} className="kd-btn-dashed" style={{ fontSize: 13 }}>
                  {busy === "decline" ? "…" : "Not now"}
                </button>
              </>
            )}
            {canApplyNow && (
              <button
                onClick={() => (clears > 0 ? setConfirmApply(true) : void applyNow())}
                disabled={busy !== null}
                className={accepted ? "kd-btn-primary" : "kd-btn-dashed"}
                style={{ fontSize: 13 }}
                title={`Applies immediately instead of on ${effective}`}
              >
                {busy === "apply" ? "Applying…" : `Apply now — replaces the ${effective} rollout, can't be undone`}
              </button>
            )}
            {(mandatory || accepted) && !needsStructureMapping && (
              <button onClick={() => { dismissNotice(instanceId, view); setDismissed(true); }} disabled={busy !== null} className="kd-btn-dashed" style={{ fontSize: 13 }}>
                Got it
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
